import { IContractTemplate } from '../entities/IContractTemplate';
import { IContractVariable } from '../entities/IContractVariable';
import { ContractAnalysisReasonEnum } from '../enums/ContractAnalysisReasonEnum';
import { ContractAnalysisStatusEnum } from '../enums/ContractAnalysisStatusEnum';
import { httpClient } from './helper/httpClient';
import {
  IPresignedPostTarget,
  uploadFileToPresignedPost,
} from './helper/presignedPostUpload';

export const CONTRACT_TEMPLATE_DOCX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const CONTRACT_TEMPLATE_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const ANALYZE_TIMEOUT_MS = 35000;

export interface IGetTemplateUploadUrlParams {
  contentType: string;
  fileSize: number;
}

export interface ICreateContractTemplateParams {
  name: string;
  docxFileKey: string;
  variablesSchema: IContractVariable[];
}

export interface IUpdateContractTemplateParams {
  name?: string;
  variablesSchema?: IContractVariable[];
  active?: boolean;
}

export interface IContractTemplateParagraph {
  index: number;
  text: string;
}

export interface IAnalyzeContractTemplateResult {
  variables: IContractVariable[];
  paragraphs: IContractTemplateParagraph[];
  analysisStatus?: ContractAnalysisStatusEnum;
  analysisReason?: ContractAnalysisReasonEnum;
}

export interface IContractTemplateParagraphsResult {
  paragraphs: IContractTemplateParagraph[];
}

class ContractTemplateService {
  private _baseURL = 'contract-templates';

  getUploadUrl(
    params: IGetTemplateUploadUrlParams,
  ): Promise<IPresignedPostTarget> {
    return httpClient.post<null, IPresignedPostTarget>(
      `${this._baseURL}/upload-url`,
      { ...params },
    );
  }

  uploadFileWithProgress(
    target: Pick<IPresignedPostTarget, 'uploadUrl' | 'fields'>,
    file: File,
    onProgress: (percent: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    return uploadFileToPresignedPost(target, file, onProgress, signal);
  }

  analyze(fileKey: string): Promise<IAnalyzeContractTemplateResult> {
    return httpClient.post<null, IAnalyzeContractTemplateResult>(
      `${this._baseURL}/analyze`,
      { fileKey },
      { timeout: ANALYZE_TIMEOUT_MS },
    );
  }

  extractParagraphs(
    fileKey: string,
  ): Promise<IContractTemplateParagraphsResult> {
    return httpClient.post<null, IContractTemplateParagraphsResult>(
      `${this._baseURL}/paragraphs`,
      { fileKey },
    );
  }

  getParagraphs(id: string): Promise<IContractTemplateParagraphsResult> {
    return httpClient.get<null, IContractTemplateParagraphsResult>(
      `${this._baseURL}/${id}/paragraphs`,
    );
  }

  create(data: ICreateContractTemplateParams): Promise<IContractTemplate> {
    return httpClient.post<null, IContractTemplate>(this._baseURL, {
      ...data,
    });
  }

  getAll(): Promise<IContractTemplate[]> {
    return httpClient.get<null, IContractTemplate[]>(this._baseURL);
  }

  getById(id: string): Promise<IContractTemplate> {
    return httpClient.get<null, IContractTemplate>(`${this._baseURL}/${id}`);
  }

  update(id: string, data: IUpdateContractTemplateParams): Promise<void> {
    return httpClient.put(`${this._baseURL}/${id}`, { ...data });
  }

  remove(id: string): Promise<void> {
    return httpClient.delete(`${this._baseURL}/${id}`);
  }
}

export default new ContractTemplateService();
